import { useEffect, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { displayName, hasCapability } from "../utils/capabilities";
import JobServiceTickets from "./JobServiceTickets";
import JobCosting from "./JobCosting";
import BackButton from "../components/BackButton";

// Tech-on-phone view of a single job. Reordered + simplified vs the desktop
// JobDetailPage: primary status action (Start / Complete / Reopen) is a huge
// tap target at the top, admin-only sections (tech assignment, JobCosting,
// schedule editing) are hidden. Admins manage those from the desktop browser.

const STATUS_LABEL = {
  unassigned: "Unassigned",
  assigned: "Assigned",
  in_progress: "In Progress",
  closed: "Completed",
  canceled: "Canceled",
};

const STATUS_COLOR = {
  unassigned: { bg: "#f3f4f6", fg: "#444" },
  assigned: { bg: "#e8f0fe", fg: "#1a5fb4" },
  in_progress: { bg: "#fff3cd", fg: "#9a6b00" },
  closed: { bg: "#c9efc9", fg: "#1a7f37" },
  canceled: { bg: "#f8d7da", fg: "#c12c2c" },
};

export default function JobDetailPageMobile({
  job,
  customers,
  jobsRaw = [],
  serviceTicketsRaw = [],
  purchaseOrdersRaw = [],
  errorMessage,
  actions,
  role,
  capabilities,
  user,
  profile,
  orgId,
  logoUrl,
  orgName,
  orgAddress = {},
  quoteDefaults,
  onBack,
}) {
  const canEditJobs = hasCapability(role, capabilities, "editJobs");
  // Split from editJobs 2026-06-15 — techs editing jobs don't auto-see
  // rates / margins. Manager tier default-on; tech tier grantable.
  const canViewEstimatedBill = hasCapability(
    role,
    capabilities,
    "viewEstimatedBill"
  );
  const canManage = role === "admin" || role === "manager";
  const isAssignedToMe = (job.assignedTechs || []).includes(user?.uid);

  // Org users — for assignment picker. Same fetch as desktop JobDetailPage.
  // Only fires when caller can edit (technicians can't read users).
  const [orgUsers, setOrgUsers] = useState([]);
  const [usersError, setUsersError] = useState("");
  useEffect(() => {
    if (!orgId || !canEditJobs) return;
    const q = query(collection(db, "users"), where("orgId", "==", orgId));
    const unsub = onSnapshot(
      q,
      (snap) => setOrgUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => {
        console.error("org users listener error:", err);
        setUsersError(err.message);
      }
    );
    return unsub;
  }, [orgId, canEditJobs]);

  const toggleTech = (uid) => {
    const current = job.assignedTechs || [];
    const next = current.includes(uid)
      ? current.filter((id) => id !== uid)
      : [...current, uid];
    const patch = {
      assignedTechs: next,
      updatedAt: new Date().toISOString(),
    };
    if (job.status === "unassigned" && next.length > 0) {
      patch.status = "assigned";
    } else if (job.status === "assigned" && next.length === 0) {
      patch.status = "unassigned";
    }
    actions.updateJob(job.id, patch);
  };

  const customer = customers.find((c) => c.id === job.customerId);
  const location = customer?.locations.find((l) => l.id === job.locationId);
  const equipmentList = (location?.equipment || []).filter((eq) =>
    (job.equipmentIds || []).includes(eq.id)
  );

  const startJob = () =>
    actions.updateJob(job.id, {
      status: "in_progress",
      updatedAt: new Date().toISOString(),
    });

  const completeJob = async () => {
    if (!window.confirm("Mark this job complete?")) return;
    await actions.updateJob(job.id, {
      status: "closed",
      updatedAt: new Date().toISOString(),
      closedAt: new Date().toISOString(),
      closedBy: user?.email || "unknown",
    });
    window.alert("Job marked complete. It moves to the Completed section.");
  };

  const reopenJob = () =>
    actions.updateJob(job.id, {
      status: (job.assignedTechs || []).length > 0 ? "assigned" : "unassigned",
      updatedAt: new Date().toISOString(),
      closedAt: null,
      closedBy: null,
      billFinalized: false,
      billFinalizedAt: null,
      billFinalizedBy: null,
    });

  const canStart =
    job.status === "assigned" && (isAssignedToMe || canEditJobs);
  const canComplete =
    job.status === "in_progress" && (isAssignedToMe || canEditJobs);
  const canReopen =
    (job.status === "closed" || job.status === "canceled") && canEditJobs;

  const statusColors = STATUS_COLOR[job.status] || STATUS_COLOR.unassigned;

  const bigButton = {
    width: "100%",
    padding: "16px",
    fontSize: "17px",
    fontWeight: 600,
    borderRadius: "8px",
    border: "none",
    cursor: "pointer",
    marginBottom: "10px",
  };

  const card = {
    border: "1px solid #e5e7eb",
    borderRadius: "10px",
    padding: "16px",
    marginBottom: "12px",
    background: "white",
  };

  return (
    <div
      style={{
        padding: "8px 12px",
        paddingBottom: canComplete ? "100px" : "8px",
        fontSize: "15px",
      }}
    >
      <BackButton onClick={onBack} />

      <h2 style={{ margin: "0 0 8px", fontSize: "20px", lineHeight: 1.25 }}>
        {job.jobNumber ? `${job.jobNumber} — ` : ""}
        {job.title}
      </h2>

      <div style={{ marginBottom: "16px" }}>
        <span
          style={{
            display: "inline-block",
            padding: "4px 10px",
            borderRadius: "999px",
            background: statusColors.bg,
            color: statusColors.fg,
            fontSize: "13px",
            fontWeight: 600,
          }}
        >
          {STATUS_LABEL[job.status] || job.status}
        </span>
        {job.jobType && (
          <span style={{ marginLeft: "8px", fontSize: "13px", color: "#666" }}>
            {job.jobType}
          </span>
        )}
        {job.quoteNumber && (
          <span style={{ marginLeft: "8px", fontSize: "13px", color: "#666" }}>
            From quote {job.quoteNumber}
          </span>
        )}
      </div>

      {/* Start Job + Reopen used to live at the top of the page; they
          now sit BELOW the Assignment card so the natural flow is
          assign-tech → Start Job → tickets. See the canStart-gated
          block further down after Assignment. */}
      {canReopen && (
        <button
          style={{
            ...bigButton,
            background: "white",
            color: "#1a5fb4",
            border: "1px solid #1a5fb4",
          }}
          onClick={reopenJob}
        >
          Reopen Job
        </button>
      )}

      {/* Where + What */}
      <div style={card}>
        <div style={{ fontSize: "16px", fontWeight: 600, marginBottom: "4px" }}>
          {customer?.name || "—"}
        </div>
        <div style={{ color: "#444", marginBottom: "10px" }}>
          📍 {location?.name || "—"}
        </div>

        {job.description && (() => {
          // Same bullet treatment as desktop: multi-line descriptions
          // render as <ul>, single line stays inline. Maintenance visits
          // get the "Scope of work" label instead of "Description".
          const lines = job.description
            .split(/\r?\n/)
            .map((s) => s.trim())
            .filter(Boolean);
          const label = job.isMaintenanceVisit
            ? "Scope of work"
            : "Description";
          return (
            <div style={{ marginBottom: "10px" }}>
              <div
                style={{
                  fontSize: "12px",
                  color: "#888",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                {label}
              </div>
              {lines.length > 1 ? (
                <ul style={{ margin: "4px 0", paddingLeft: "20px" }}>
                  {lines.map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
                </ul>
              ) : (
                <div>{job.description}</div>
              )}
            </div>
          );
        })()}

        <div>
          <div
            style={{
              fontSize: "12px",
              color: "#888",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              marginBottom: "4px",
            }}
          >
            Equipment ({equipmentList.length})
          </div>
          {equipmentList.length === 0 ? (
            <div style={{ color: "#666" }}>None</div>
          ) : (
            <ul style={{ margin: 0, paddingLeft: "20px" }}>
              {equipmentList.map((eq) => (
                <li key={eq.id} style={{ marginBottom: "2px" }}>
                  {eq.type}
                  {eq.subType ? ` (${eq.subType})` : ""}
                </li>
              ))}
            </ul>
          )}
        </div>

        {(job.scheduledDate || job.estimatedPartsArrival) && (
          <div style={{ marginTop: "12px", fontSize: "13px", color: "#444" }}>
            {job.scheduledDate && (
              <div>
                <strong>Scheduled:</strong> {job.scheduledDate}
              </div>
            )}
            {job.estimatedPartsArrival && (
              <div>
                <strong>Parts ETA:</strong> {job.estimatedPartsArrival}
              </div>
            )}
          </div>
        )}

        {job.updateUponDiagnostic && (
          <div
            style={{
              marginTop: "12px",
              padding: "10px",
              background: "#fff3cd",
              border: "1px solid #f1d775",
              borderRadius: "6px",
              fontSize: "13px",
              color: "#9a6b00",
              fontWeight: 600,
            }}
          >
            ⚠ Update upon Diagnostic — confirm equipment/scope after the
            on-site visit.
          </div>
        )}
      </div>

      {/* Assign technicians — admin/manager only (editJobs). Same listener +
          toggle as the desktop JobDetailPage. Hidden on tech accounts so the
          main tech flow stays uncluttered. */}
      {canEditJobs && (
        <div style={card}>
          <div
            style={{
              fontSize: "12px",
              color: "#888",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              marginBottom: "8px",
            }}
          >
            Assigned Technicians
          </div>
          {usersError && (
            <div style={{ color: "red", fontSize: "12px", marginBottom: "6px" }}>
              {usersError}
            </div>
          )}
          {orgUsers.filter(
            (u) => !u.deleted && u.fieldEligible !== false
          ).length === 0 ? (
            <div style={{ fontSize: "13px", color: "#666" }}>
              No team members to assign. (Office staff are filtered out —
              toggle Field-eligible in ManageUsersPage to add someone.)
            </div>
          ) : (
            orgUsers
              .filter((u) => !u.deleted && u.fieldEligible !== false)
              .map((u) => (
                <label
                  key={u.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    fontSize: "15px",
                    padding: "8px 0",
                    borderBottom: "1px solid #f3f4f6",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={(job.assignedTechs || []).includes(u.id)}
                    onChange={() => toggleTech(u.id)}
                    style={{
                      marginRight: "10px",
                      width: "20px",
                      height: "20px",
                    }}
                  />
                  <span>
                    {displayName(u.profile, u.email)}{" "}
                    <span style={{ color: "#888", fontSize: "13px" }}>
                      ({u.role})
                    </span>
                  </span>
                </label>
              ))
          )}
        </div>
      )}

      {/* Start Job — moved below Assignment so the flow is assign-tech
          → Start Job → tickets unlock. canStart already requires the
          status to be "assigned" (which only happens after at least
          one tech is on assignedTechs). */}
      {canStart && (
        <button
          style={{ ...bigButton, background: "#1a5fb4", color: "white" }}
          onClick={startJob}
        >
          ▶  Start Job
        </button>
      )}

      {/* Service tickets — locked until the job is IN PROGRESS (started)
          AND while the bill is not finalized. Pre-start and post-finalize
          both prevent edits — same gate as desktop JobDetailPage. */}
      <JobServiceTickets
        job={job}
        tickets={serviceTicketsRaw.filter((t) => t.jobId === job.id)}
        equipmentList={equipmentList}
        actions={actions}
        user={user}
        profile={profile}
        canFile={
          (isAssignedToMe || canEditJobs) &&
          job.status === "in_progress" &&
          !job.billFinalized
        }
        errorMessage={errorMessage}
        customer={customer}
        location={location}
        customerName={customer?.name}
        locationName={location?.name}
        jobsRaw={jobsRaw}
        canManage={canManage}
        role={role}
        capabilities={capabilities}
        logoUrl={logoUrl}
        orgName={orgName}
        orgAddress={orgAddress}
        jobPOs={purchaseOrdersRaw.filter((po) => po.jobId === job.id)}
        orgUsers={orgUsers}
      />

      {/* Estimated bill — viewEstimatedBill cap (split from editJobs
          per Justin 2026-06-15). Manager tier default-on; tech tier
          default-off but grantable for light commercial / residential
          shops where techs also estimate. */}
      {canViewEstimatedBill && (
        <JobCosting
          job={job}
          actions={actions}
          quoteDefaults={quoteDefaults}
          customer={customer}
          location={location}
          user={user}
          role={role}
          capabilities={capabilities}
          profile={profile}
          logoUrl={logoUrl}
          orgName={orgName}
          orgAddress={orgAddress}
        />
      )}

      {/* Sticky bottom: Mark Complete is the END action — keep it always
          visible at the bottom of the viewport so the tech can finish the
          job from anywhere on the page without scrolling back up. */}
      {canComplete && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            padding: "12px 16px",
            background: "rgba(255,255,255,0.97)",
            borderTop: "1px solid #e5e7eb",
            boxShadow: "0 -2px 10px rgba(0,0,0,0.06)",
            zIndex: 100,
          }}
        >
          <button
            style={{ ...bigButton, background: "#1a7f37", color: "white", marginBottom: 0 }}
            onClick={completeJob}
          >
            ✓  Mark Complete
          </button>
        </div>
      )}
    </div>
  );
}
